import { useState, useEffect } from 'react';

interface Logo {
  url: string;
  filename: string;
  uploadedAt: string;
}

interface LogoPickerProps {
  onSelect: (url: string) => void;
  onClose: () => void;
  currentUrl?: string;
}

export default function LogoPicker({ onSelect, onClose, currentUrl }: LogoPickerProps) {
  const [logos, setLogos] = useState<Logo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const [maxSizeMB, setMaxSizeMB] = useState(1);
  const MAX_LOGOS = 50;

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      const response = await fetch('/api/upload/logos', {
        credentials: 'include',
      });
      const result = await response.json();
      if (result.success) {
        setLogos(result.data.logos || result.data);
        if (result.data.maxSizeMB) {
          setMaxSizeMB(result.data.maxSizeMB);
        }
      }
    } catch (error) {
      console.error('Failed to load logos:', error);
    }
  };

  const handleUpload = async (file: File) => {
    console.log('Starting upload for file:', file.name);
    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Sending upload request...');
      const response = await fetch('/api/upload/logo', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      console.log('Upload response status:', response.status);
      const result = await response.json();
      console.log('Upload result:', result);

      if (!result.success) {
        setUploadError(result.error || 'Upload failed');
        return;
      }

      // Reload logos and select the new one
      console.log('Reloading logos...');
      await loadLogos();
      console.log('Selecting uploaded logo:', result.data.url);
      onSelect(result.data.url);
    } catch (error) {
      setUploadError('Failed to upload logo');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
      // Reset input so same file can be uploaded again
      e.target.value = '';
    }
  };

  const handleDelete = async (logoUrl: string) => {
    if (!confirm('Are you sure you want to delete this logo?')) {
      return;
    }

    setDeletingUrl(logoUrl);
    try {
      const response = await fetch('/api/upload/logo', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: logoUrl }),
        credentials: 'include',
      });

      const result = await response.json();
      if (!result.success) {
        alert(result.error || 'Failed to delete logo');
        return;
      }

      // Reload logos after deletion
      await loadLogos();
    } catch (error) {
      console.error('Failed to delete logo:', error);
      alert('Failed to delete logo');
    } finally {
      setDeletingUrl(null);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>Logo Library</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            ×
          </button>
        </div>

        {/* Upload Button */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <input
                type="file"
                id="logo-upload-input"
                accept="image/*"
                onChange={handleFileInput}
                style={{ display: 'none' }}
                disabled={uploading || logos.length >= MAX_LOGOS}
              />
              <button
                type="button"
                onClick={() => document.getElementById('logo-upload-input')?.click()}
                disabled={uploading || logos.length >= MAX_LOGOS}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (uploading || logos.length >= MAX_LOGOS) ? '#ccc' : '#0066cc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (uploading || logos.length >= MAX_LOGOS) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: (uploading || logos.length >= MAX_LOGOS) ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                {uploading ? 'Uploading...' : 'Upload New Logo'}
              </button>
            </div>
            <div style={{ fontSize: '13px', color: logos.length >= MAX_LOGOS ? '#dc3545' : '#666', fontWeight: '500' }}>
              {logos.length} / {MAX_LOGOS} logos
            </div>
          </div>
          {uploadError && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '8px' }}>
              {uploadError}
            </div>
          )}
          {logos.length >= MAX_LOGOS && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '8px', fontWeight: '500' }}>
              Maximum logo limit reached. Please delete existing logos to upload new ones.
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#666' }}>
            Max {maxSizeMB}MB • PNG, JPG, GIF, WebP
          </div>
        </div>

        {/* Logo Grid */}
        {logos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</div>
            <p>No logos uploaded yet</p>
            <p style={{ fontSize: '14px' }}>Upload your first logo to get started</p>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '16px',
            }}
          >
            {logos.map((logo) => (
              <div
                key={logo.url}
                style={{
                  border: currentUrl === logo.url ? '3px solid #0066cc' : '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: currentUrl === logo.url ? '#f0f8ff' : 'white',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
              >
                {/* Delete button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(logo.url);
                  }}
                  disabled={deletingUrl === logo.url}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '4px 8px',
                    backgroundColor: deletingUrl === logo.url ? '#ccc' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deletingUrl === logo.url ? 'not-allowed' : 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    zIndex: 10,
                  }}
                  title="Delete logo"
                >
                  {deletingUrl === logo.url ? '...' : '×'}
                </button>
                
                {/* Clickable logo area */}
                <div
                  onClick={() => {
                    onSelect(logo.url);
                    onClose();
                  }}
                  style={{
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (currentUrl !== logo.url) {
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.style.borderColor = '#999';
                        parent.style.backgroundColor = '#f9f9f9';
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentUrl !== logo.url) {
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.style.borderColor = '#ddd';
                        parent.style.backgroundColor = 'white';
                      }
                    }
                  }}
                >
                  <img
                    src={logo.url}
                    alt={logo.filename}
                    style={{
                      width: '100%',
                      height: '100px',
                      objectFit: 'contain',
                      marginBottom: '8px',
                    }}
                  />
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={logo.filename}
                  >
                    {logo.filename}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
